import { supabase } from "./supabase";

// Notification types
export const NOTIFICATION_TYPES = {
  SYSTEM: "system",
  MESSAGE: "message",
  WORKSPACE: "workspace",
  SUBSCRIPTION: "subscription",
  ASSISTANT: "assistant",
};

// Notification status types
export const NOTIFICATION_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  FAILED: "failed",
};

/**
 * Helper function to create a notification
 * @param {object} params - Notification parameters
 * @param {string} params.userId - User ID (required)
 * @param {string} params.type - Notification type
 * @param {string} params.title - Notification title
 * @param {string} params.message - Notification message
 * @param {object} params.data - Additional notification data
 * @param {string} params.senderId - Sender ID (optional)
 * @returns {Promise<object>} - Response with success boolean and data/error
 */
export const createNotification = async ({
  userId,
  type = NOTIFICATION_TYPES.SYSTEM,
  title,
  message,
  data = {},
  senderId = null,
}) => {
  if (!userId) {
    console.error("User ID is required to create a notification");
    return { success: false, error: "User ID is required" };
  }

  try {
    // Prepare notification object
    const notification = {
      user_id: userId,
      sender_id: senderId,
      type,
      content: {
        title,
        message,
        ...data,
      },
      status: NOTIFICATION_STATUS.COMPLETED,
      is_read: false,
    };

    // Insert into Supabase
    const { data: createdNotification, error } = await supabase
      .from("notifications")
      .insert([notification])
      .select();

    if (error) throw error;

    return {
      success: true,
      data: createdNotification?.[0] || null,
    };
  } catch (error) {
    console.error("Error creating notification:", error);
    return {
      success: false,
      error: error.message || "Failed to create notification",
    };
  }
};

/**
 * Get notifications for a user
 * @param {string} userId - User ID
 * @param {object} options - Query options
 * @param {number} options.limit - Number of notifications to fetch
 * @param {number} options.offset - Offset for pagination
 * @param {string} options.type - Filter by notification type
 * @param {boolean} options.unreadOnly - Fetch only unread notifications
 * @returns {Promise<object>} - Response with success boolean and data/error
 */
export const getUserNotifications = async (
  userId,
  { limit = 50, offset = 0, type = null, unreadOnly = false } = {}
) => {
  if (!userId) {
    return { success: false, error: "User ID is required" };
  }

  try {
    // Build query
    let query = supabase
      .from("notifications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    // Apply filters if provided
    if (type) {
      query = query.eq("type", type);
    }

    if (unreadOnly) {
      query = query.eq("is_read", false);
    }

    // Execute query
    const { data, error } = await query;

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch notifications",
    };
  }
};

/**
 * Mark notifications as read
 * @param {object} params - Parameters
 * @param {string} params.userId - User ID
 * @param {string} params.notificationId - Single notification ID to mark as read (optional)
 * @param {string[]} params.notificationIds - Array of notification IDs to mark as read (optional)
 * @param {boolean} params.markAll - Mark all user's notifications as read (default: false)
 * @returns {Promise<object>} - Response with success boolean and data/error
 */
export const markNotificationsAsRead = async ({
  userId,
  notificationId = null,
  notificationIds = [],
  markAll = false,
}) => {
  if (!userId) {
    return { success: false, error: "User ID is required" };
  }

  try {
    // Build query
    let query = supabase.from("notifications").update({
      is_read: true,
      updated_at: new Date().toISOString(),
    });

    // Apply filters based on parameters
    if (markAll) {
      // Mark all notifications for this user as read
      query = query.eq("user_id", userId).eq("is_read", false);
    } else if (notificationId) {
      // Mark single notification as read
      query = query.eq("id", notificationId).eq("user_id", userId);
    } else if (notificationIds && notificationIds.length > 0) {
      // Mark multiple notifications as read
      query = query.in("id", notificationIds).eq("user_id", userId);
    } else {
      // No valid filter specified
      return {
        success: false,
        error:
          "At least one of: notificationId, notificationIds, or markAll is required",
      };
    }

    // Execute update
    const { data, error } = await query;

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error("Error marking notifications as read:", error);
    return {
      success: false,
      error: error.message || "Failed to mark notifications as read",
    };
  }
};

/**
 * Delete a notification
 * @param {string} notificationId - Notification ID to delete
 * @param {string} userId - User ID for verification
 * @returns {Promise<object>} - Response with success boolean and data/error
 */
export const deleteNotification = async (notificationId, userId) => {
  if (!notificationId || !userId) {
    return {
      success: false,
      error: "Notification ID and User ID are required",
    };
  }

  try {
    // Delete the notification
    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("id", notificationId)
      .eq("user_id", userId); // Ensure user can only delete their own notifications

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error("Error deleting notification:", error);
    return {
      success: false,
      error: error.message || "Failed to delete notification",
    };
  }
};

/**
 * Setup real-time subscription for notifications
 * @param {string} userId - User ID
 * @param {function} onInsert - Callback for new notifications
 * @param {function} onUpdate - Callback for updated notifications
 * @param {function} onDelete - Callback for deleted notifications
 * @returns {object} - Subscription object that can be used to unsubscribe
 */
export const subscribeToNotifications = (
  userId,
  onInsert,
  onUpdate,
  onDelete
) => {
  if (!userId) {
    console.error("User ID is required for notification subscription");
    return null;
  }

  // Create subscription
  const subscription = supabase
    .channel(`notifications-${userId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        if (onInsert && typeof onInsert === "function") {
          onInsert(payload.new);
        }
      }
    )
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        if (onUpdate && typeof onUpdate === "function") {
          onUpdate(payload.old, payload.new);
        }
      }
    )
    .on(
      "postgres_changes",
      {
        event: "DELETE",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        if (onDelete && typeof onDelete === "function") {
          onDelete(payload.old);
        }
      }
    )
    .subscribe();

  return subscription;
};

/**
 * Get the count of unread notifications
 * @param {string} userId - User ID
 * @returns {Promise<object>} - Response with success boolean and count/error
 */
export const getUnreadNotificationCount = async (userId) => {
  if (!userId) {
    return { success: false, error: "User ID is required" };
  }

  try {
    // Count unread notifications
    const { count, error } = await supabase
      .from("notifications")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("is_read", false);

    if (error) throw error;

    return { success: true, count: count || 0 };
  } catch (error) {
    console.error("Error counting unread notifications:", error);
    return {
      success: false,
      error: error.message || "Failed to count unread notifications",
    };
  }
};

// Export a notifications utility object
export const NotificationsUtils = {
  createNotification,
  getUserNotifications,
  markNotificationsAsRead,
  deleteNotification,
  subscribeToNotifications,
  getUnreadNotificationCount,
  TYPES: NOTIFICATION_TYPES,
  STATUS: NOTIFICATION_STATUS,
};
